/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './PrivacyPolicy.css';

class PrivacyPolicy extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  render() {
    const privacyEmail = () => (
      <span
        // Keep the contact address readable for app review crawlers.
        dangerouslySetInnerHTML={{
          __html:
            '<!--email_off--><a href="mailto:contact@habitualapp.io">contact@habitualapp.io</a><!--/email_off-->',
        }}
      />
    );

    return (
      <div className={s.root}>
        <div className={s.container}>
          <h1>{this.props.title}</h1>
          <p>
            <strong>App name:</strong> Habit Tracker · Daily Planner
            (also written as Habit Tracker - Daily Planner)
          </p>
          <p>
            <strong>Android package name:</strong>{' '}
            com.holgersindbaek.habitual
          </p>
          <p>
            <strong>Google Play developer name:</strong> Online Solitaire
          </p>
          <p>
            <strong>Developer and legal operator:</strong> Online Solitaire is
            owned and operated by Holger Sindbaek ApS, Lyngbyvej 42, 1 th,
            2100 København Ø, Denmark, CVR: DK-42330949.
          </p>
          <p>
            <strong>Privacy contact:</strong> {privacyEmail()}
          </p>
          <p>
            This Privacy Policy applies to Habit Tracker · Daily Planner
            (package name com.holgersindbaek.habitual), also known as Habitual.
            The app is published on Google Play by Online Solitaire. Habitual
            is operated by Holger Sindbaek.
          </p>
          <p>Last updated: May 5, 2026</p>
          <p>
            This page is used to inform visitors regarding my policies with the
            collection, use, and disclosure of Personal Information if anyone
            decided to use my Service.
          </p>
          <p>
            If you choose to use my Service, then you agree to the collection
            and use of information in relation to this policy. The Personal
            Information that I collect is used for providing and improving the
            Service. I will not use or share your information with anyone except
            as described in this Privacy Policy.
          </p>
          <p>
            The terms used in this Privacy Policy have the same meanings as in
            our Terms and Conditions, which is accessible at Habitual unless
            otherwise defined in this Privacy Policy.
          </p>
          <p>
            <strong>Information Collection and Use</strong>
          </p>
          <p>
            For a better experience, while using our Service, I may ask you to
            provide certain personally identifiable information, including your
            name and email address. The app may also collect account identifiers,
            habit and routine content, settings, reminder preferences, product
            interaction data, crash data, diagnostics, and device information
            needed to provide, sync, secure, improve, and troubleshoot the
            Service.
          </p>
          <p>
            Habit Tracker · Daily Planner does not sell your personal
            information and does not use your data for cross-app or cross-site
            advertising tracking.
          </p>
          <p>
            The app uses third party services that may collect or process
            information used to provide the Service, analyze how the Service is
            used, and diagnose crashes or technical problems.
          </p>
          <div>
            <p>
              Link to privacy policy of third party service providers used by
              the app
            </p>
            <ul>
              <li>
                <a
                  href="https://firebase.google.com/support/privacy/"
                  target="_blank"
                >
                  Firebase
                </a>
              </li>
              <li>
                <a
                  href="https://openpanel.dev/privacy"
                  target="_blank"
                >
                  OpenPanel
                </a>
              </li>
            </ul>
          </div>
          <p>
            <strong>Log Data</strong>
          </p>
          <p>
            I want to inform you that whenever you use my Service, in a case of
            an error in the app I collect data and information (through third
            party products) on your phone called Log Data. This Log Data may
            include information such as your device Internet Protocol (“IP”)
            address, device name, operating system version, the configuration of
            the app when utilizing my Service, the time and date of your use of
            the Service, and other statistics.
          </p>
          <p>
            <strong>Cookies</strong>
          </p>
          <p>
            Cookies are files with a small amount of data that are commonly used
            as anonymous unique identifiers. These are sent to your browser from
            the websites that you visit and are stored on your device's internal
            memory.
          </p>
          <p>
            This Service does not use these “cookies” explicitly. However, the
            app may use third party code and libraries that use “cookies” to
            collect information and improve their services. You have the option
            to either accept or refuse these cookies and know when a cookie is
            being sent to your device. If you choose to refuse our cookies, you
            may not be able to use some portions of this Service.
          </p>
          <p>
            <strong>Service Providers</strong>
          </p>
          <p>
            I may employ third-party companies and individuals due to the
            following reasons:
          </p>
          <ul>
            <li>To facilitate our Service;</li>
            <li>To provide the Service on our behalf;</li>
            <li>To perform Service-related services; or</li>
            <li>To assist us in analyzing how our Service is used.</li>
          </ul>
          <p>
            I want to inform users of this Service that these third parties have
            access to your Personal Information. The reason is to perform the
            tasks assigned to them on our behalf. However, they are obligated
            not to disclose or use the information for any other purpose.
          </p>
          <p>
            <strong>Security</strong>
          </p>
          <p>
            I value your trust in providing us your Personal Information, thus
            we are striving to use commercially acceptable means of protecting
            it. But remember that no method of transmission over the internet,
            or method of electronic storage is 100% secure and reliable, and I
            cannot guarantee its absolute security.
          </p>
          <p>
            <strong>Data Retention and Deletion</strong>
          </p>
          <p>
            I retain account information, habit and routine content, settings,
            diagnostics, and related service data for as long as needed to
            provide the Service, maintain backups, troubleshoot issues, protect
            the Service, comply with legal obligations, or resolve disputes.
            You may request deletion of your account and associated personal
            data from within the app where available, or by contacting{' '}
            {privacyEmail()}. When an account deletion request is completed,
            personal data associated with the account is deleted or anonymized
            unless retention is required for legal, security, fraud prevention,
            or operational reasons.
          </p>
          <p>
            <strong>Links to Other Sites</strong>
          </p>
          <p>
            This Service may contain links to other sites. If you click on a
            third-party link, you will be directed to that site. Note that these
            external sites are not operated by me. Therefore, I strongly advise
            you to review the Privacy Policy of these websites. I have no
            control over and assume no responsibility for the content, privacy
            policies, or practices of any third-party sites or services.
          </p>
          <p>
            <strong>Children’s Privacy</strong>
          </p>
          <p>
            These Services do not address anyone under the age of 13. I do not
            knowingly collect personally identifiable information from children
            under 13. In the case I discover that a child under 13 has provided
            me with personal information, I immediately delete this from our
            servers. If you are a parent or guardian and you are aware that your
            child has provided us with personal information, please contact me
            so that I will be able to do necessary actions.
          </p>
          <p>
            <strong>Changes to This Privacy Policy</strong>
          </p>
          <p>
            I may update our Privacy Policy from time to time. Thus, you are
            advised to review this page periodically for any changes. I will
            notify you of any changes by posting the new Privacy Policy on this
            page. These changes are effective immediately after they are posted
            on this page.
          </p>
          <p>
            <strong>Contact Us</strong>
          </p>
          <p>
            If you have any questions or suggestions about my Privacy Policy, do
            not hesitate to contact me at {privacyEmail()}.
          </p>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(PrivacyPolicy);
